/**
 * <es-carousel> - arrow controls for a native scroll-snap track.
 *
 * The scrolling itself is plain CSS (overflow + scroll-snap), so swipe, touch, trackpad, wheel and keyboard
 * scrolling all work with no script at all. This element only adds what CSS cannot: previous / next buttons
 * and keeping their disabled state honest. If the script never loads the track is still fully usable.
 *
 * Used by Featured Collection and Collection List. Slideshow does not use it - its behaviour (one visible
 * slide, autoplay, fade) is different enough that sharing an abstraction would serve neither.
 *
 * Markup contract:
 *   <es-carousel>
 *     <div data-carousel-track> ...items... </div>
 *     <div data-carousel-controls>
 *       <button data-carousel-prev> ... </button>
 *       <button data-carousel-next> ... </button>
 *     </div>
 *   </es-carousel>
 *
 * Direction: "next" always means toward the inline END, which is the left in Arabic. Browsers report
 * scrollLeft as 0 at the start and negative going forward in RTL, so positions are compared by absolute
 * value and the scroll direction is derived from the computed writing direction.
 */
class ESCarousel extends HTMLElement {
  constructor() {
    super();
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.update = this.update.bind(this);

    this.track = null;
    this.controls = null;
    this.prevButton = null;
    this.nextButton = null;
    this.resizeObserver = null;
    this.frame = null;
  }

  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    this.controls = this.querySelector('[data-carousel-controls]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');

    if (!this.track) return;

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);

    /* Column count changes at breakpoints and images/fonts settle after load; observing the track keeps the
       arrows correct without listening to window resize. */
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
    }

    this.update();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    this.track = null;
    this.controls = null;
    this.prevButton = null;
    this.nextButton = null;
  }

  get isRtl() {
    return this.track ? getComputedStyle(this.track).direction === 'rtl' : false;
  }

  get behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  onPrevClick() {
    this.scrollByPage(-1);
  }

  onNextClick() {
    this.scrollByPage(1);
  }

  /* One "page" is the width of the visible area, so each press reveals a fresh set of items. Snap points
     then settle the track on an item edge. */
  scrollByPage(direction) {
    if (!this.track) return;

    const inline = this.isRtl ? -1 : 1;
    this.track.scrollBy({
      left: direction * inline * this.track.clientWidth,
      behavior: this.behavior,
    });
  }

  /* Coalesce bursts of scroll events into one measurement per frame. */
  onScroll() {
    if (this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.update();
    });
  }

  update() {
    if (!this.track) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);
    const scrollable = maxScroll > 1;

    /* Nothing to scroll (all items fit): hide the controls entirely rather than show two dead buttons. */
    if (this.controls) this.controls.hidden = !scrollable;

    if (this.prevButton) this.prevButton.disabled = !scrollable || position <= 1;
    if (this.nextButton) this.nextButton.disabled = !scrollable || position >= maxScroll - 1;
  }
}

if (!customElements.get('es-carousel')) {
  customElements.define('es-carousel', ESCarousel);
}
